// ── showService ───────────────────────────────────────────────────────────────
// "Which of MY streaming services carry this show, and how do I open one?"
//
// Extracted from StreamingLaunchButton so a second caller can reuse the answer
// instead of re-deriving it. WatchTonight's ticket now needs the same fact to make
// its primary CTA actually launch the service (Jason 2026-07-29: the gold "Admit one"
// bar looked like the main action while the real one was a 26px logo in the corner).
// Duplicating the provider fetch + filter in two places is exactly how the two would
// have drifted apart.
//
// DELIBERATE RESTRAINT, carried over unchanged: only services the user actually
// SUBSCRIBES TO are ever offered. We know every provider TMDB lists for a show, but
// sending someone to a service they don't pay for is worse than saying nothing.
//
// The pure part lives here and is unit-tested; the React hook is a thin wrapper.

// Match my services against the providers TMDB lists for a show.
// `cap` bounds how many are rendered inline; the rest are reported as `extra` rather
// than silently dropped (the "+N" chip, Jason 2026-07-22).
export function pickServices(myList, providerIds, cap = 2) {
  const all = (myList || []).filter(s => (providerIds || []).includes(s.id))
  const shown = cap > 0 ? all.slice(0, cap) : all
  return { all, shown, extra: all.length - shown.length, primary: shown[0] || null }
}

// Open a service: native app via deep link, with a web fallback a beat later in case
// the app isn't installed. Kept verbatim from StreamingLaunchButton so behaviour is
// identical for both callers.
export function launchService(svc, showName, { onRecord } = {}) {
  if (!svc) return false
  onRecord?.({ service: svc.name })
  const webUrl = svc.search ? svc.search(showName) : '#'
  if (svc.deepLink) {
    window.location.href = svc.deepLink(showName)
    setTimeout(() => window.open(webUrl, '_blank'), 1000)
  } else {
    window.open(webUrl, '_blank')
  }
  return true
}

// Where ELSE a show (or season) streams when none of MY services carry it. The
// restraint rule above still holds — we never present a service you don't pay for
// as if it were yours — but "it's only on Philo" is the answer Jason had to go dig
// out of Apple TV by hand (2026-11-01). Returns one registry service, the first in
// registry order that TMDB lists and that is not already mine, or null.
export function whereElse(registry, myList, providerIds) {
  const ids = new Set(providerIds || [])
  const mine = new Set((myList || []).map(s => s.id))
  if ([...mine].some(id => ids.has(id))) return null   // yours carries it: nothing to add
  return (registry || []).find(s => ids.has(s.id)) || null
}
